import { useWrapAsync } from "@paroicms/front-app-log";
import { useEffect } from "react";
import { fetchUsers } from "../../../../api-requests/users";
import { deleteUser } from "../../../../api/application/user";
import {
  type Pagination,
  type PaginationOptions,
  apiRequestWrapper,
} from "../../../../api/shared/http-client";
import type { User } from "../../../../helpers/format-users";
import { useAppStore } from "../../../../store/app.store";
import { syncOpenModalDialog } from "../../../shared/global-tools/dialog-system";
import { showToast } from "../../../shared/global-tools/toast-system";
import Navbar from "../../parts/Navbar/Navbar";
import makeConfirmDialog from "../../utils/ConfirmDialog/ConfirmDialog";
import Icon from "../../utils/Icons/Icon";
import OurDataTable, { type ColumnType } from "../../utils/OurDataTable/OurDataTable";
import Spinner from "../../utils/Spinner/Spinner";
import CreateUser from "./CreateUser/CreateUser";
import EditUser from "./EditUser/EditUser";

export default function UserManagementScreen() {
  const limit = 10;
  const wrapAsync = useWrapAsync();
  const users = useAppStore((state) => state.users);
  const setUsers = useAppStore((state) => state.setUsers);

  const sortedUsers = users
    ? [...users].sort((a, b) => {
        const lastNameCompare = a.lastname.localeCompare(b.lastname);
        if (lastNameCompare !== 0) return lastNameCompare;
        return a.firstname.localeCompare(b.firstname);
      })
    : undefined;

  const loadData = async (_options: PaginationOptions): Promise<Pagination> => {
    const res = await fetchUsers({ limit: 1000, page: 1 });
    setUsers(res.data);
    return {
      perPage: limit,
      total: res.data.length,
    };
  };
  const refreshUsers = () => {
    setUsers(undefined);
  };

  const handleUserDeletion = wrapAsync(async (user: User) => {
    try {
      await deleteUser(user.id);
      refreshUsers();
      showToast("Utilisateur supprimé avec succès", { severity: "success" });
    } catch (error) {
      console.error("Erreur lors de la suppression de l'utilisateur :", error);
      showToast("Échec de la suppression de l'utilisateur", { severity: "error" });
    }
  });

  useEffect(
    wrapAsync(async () => {
      if (users) return;
      await apiRequestWrapper(async () => {
        await loadData({
          limit: 1000,
          page: 1,
        });
      });
    }),
    [users],
  );

  const editActionBodyTemplate = (user: User) => (
    <div style={{ display: "flex", gap: "15px" }}>
      <EditUser user={user} refreshUsers={refreshUsers} />

      {!user.roles.some((role) => role.name === "ROLE SUPER ADMIN") && (
        <button
          type="button"
          className="IconBtn"
          onClick={() =>
            syncOpenModalDialog(makeConfirmDialog, {
              onConfirm: () => handleUserDeletion(user),
              warningMessage: "Voulez-vous vraiment supprimer cet utilisateur ?",
            })
          }
        >
          <Icon name="trash" color="#EF0404" />
        </button>
      )}
    </div>
  );
  const localIdBodyTemplate = (_referenceDatum: User, options: { rowIndex: number }) => (
    <>{options.rowIndex + 1}</>
  );
  const columns: ColumnType<User>[] = [
    {
      field: "localId",
      header: "N°",
      body: localIdBodyTemplate,
    },
    {
      field: "lastname",
      header: "Nom",
    },
    {
      field: "firstname",
      header: "Prénom",
    },
    {
      field: "email",
      header: "Email",
    },
    {
      field: "role",
      header: "Role",
      body: (user) => (
        <>
          {user.roles.map((r) => (
            <span className="mr-2" key={r.id}>
              {r.name}
            </span>
          ))}
        </>
      ),
    },
    {
      field: "editAction",
      header: "Action",
      body: editActionBodyTemplate,
    },
  ];

  if (!sortedUsers) return <Spinner />;
  return (
    <div>
      <Navbar pageTitle="Gestion des Utilisateurs" />

      <div className="Container" style={{ paddingBottom: "50px" }}>
        <div className="Container-content">
          <CreateUser refreshUsers={refreshUsers} />

          <div className="Card mt-4">
            <OurDataTable<User>
              className="mt-4"
              data={sortedUsers}
              columns={columns}
              options={{
                rows: limit,
                stripedRows: true,
                tableStyle: { minWidth: "40rem" },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
